import axios from "axios";
import { authClientWeb } from "react-redux-django-auth/web";

export type ServiceType = 'NERD' | 'AFFIDAVIT' | 'CAC' | 'TIN' | 'WEB_DESIGN' | 'PROJECT_CONVERSION' | 'OTHER' | string;

export interface ServiceRate {
  id: number;
  code: string;
  name: string;
  base_commission: string;
  default_price?: string;
  default_cost?: string;
  is_active: boolean;
  order: number;
  created_at?: string;
  updated_at?: string;
}

export type JobStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
export type PaymentStatus = 'PAID' | 'UNPAID' | 'PARTIAL';

export interface AgentPriceTier {
  id?: number;
  service_type: ServiceType | string;
  service_type_name?: string;
  agreed_price: string;
}

export interface Agent {
  id: number;
  name: string;
  phone_number?: string | null;
  email?: string | null;
  price_tiers?: AgentPriceTier[];
  created_at?: string;
}

export type AssignmentStageStatus = 'ACTIVE' | 'HANDED_OFF' | 'COMPLETED' | 'CANCELLED';

export interface DeliverableAssignment {
  id: number;
  deliverable: number;
  staff: number;
  staff_name: string;
  staff_email: string;
  stage_status: AssignmentStageStatus;
  split_percentage: string;
  allocated_commission: string;
  handoff_reason?: string | null;
  assigned_at: string;
  handed_off_at?: string | null;
  is_approved: boolean;
  approved_by?: number | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
  is_commission_paid: boolean;
  commission_paid_at?: string | null;
  commission_paid_by?: number | null;
  commission_paid_by_name?: string | null;
  payout_month?: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserSplitInfo {
  assignment_id: number;
  stage_status: AssignmentStageStatus;
  split_percentage: string;
  allocated_commission: string;
  handoff_reason?: string | null;
  is_approved: boolean;
  is_commission_paid: boolean;
}

export interface HandoffPayload {
  successor_staff_id: number;
  handoff_reason?: string;
  predecessor_split_percent?: number | string;
  successor_split_percent?: number | string;
}

export type ReferralSource = 'DIRECT' | 'WALK_IN' | 'STAFF' | 'AGENT';

export interface Job {
  id: number;
  sn: number;
  date: string;
  customer_name: string;
  job_type: ServiceType | string;
  job_type_name?: string;
  referral_source?: ReferralSource;
  agent_profile?: number | null;
  agent_profile_name?: string | null;
  is_staff_referral: boolean;
  assigned_staff?: number | null;
  assigned_staff_name?: string | null;
  amount_paid?: string;
  payment_status?: PaymentStatus;
  job_cost?: string;
  agreed_price?: string | null;
  is_approved: boolean;
  approved_by?: number | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
  is_commission_paid: boolean;
  commission_paid_at?: string | null;
  commission_paid_by?: number | null;
  commission_paid_by_name?: string | null;
  expected_commission?: string;
  commission: string;
  profit?: string;
  status: JobStatus;
  remark?: string | null;
  assignments?: DeliverableAssignment[];
  is_split?: boolean;
  user_split?: UserSplitInfo;
  created_at?: string;
  updated_at?: string;
}

export interface JobPayload {
  date?: string;
  customer_name: string;
  job_type: ServiceType;
  referral_source?: ReferralSource;
  agent_profile?: number | null;
  is_staff_referral?: boolean;
  assigned_staff?: number | null;
  amount_paid?: string | number;
  payment_status?: PaymentStatus;
  job_cost?: string | number;
  commission?: string | number;
  status?: JobStatus;
  remark?: string;
  is_approved?: boolean;
  is_commission_paid?: boolean;
  scope?: 'personal' | 'all';
}

export interface MonthlyStaffPayout {
  staff_id: number;
  staff_name: string;
  staff_email: string;
  total_jobs: number;
  approved_jobs_count: number;
  total_earned: string;
  total_paid: string;
  pending_payout: string;
  payout_status: 'FULLY_PAID' | 'PENDING_PAYOUT' | 'NO_COMMISSION';
  unpaid_job_ids: number[];
}

export interface MonthlyPayoutSummary {
  month: string;
  total_jobs?: number;
  completed_jobs?: number;
  pending_jobs_count?: number;
  approved_jobs_count?: number;
  total_earned?: string;
  total_paid?: string;
  pending_payout?: string;
  awaiting_approval_count?: number;
  summary?: {
    total_jobs: number;
    pending_jobs_count?: number;
    approved_jobs_count: number;
    total_earned: string;
    total_paid: string;
    total_pending_payout: string;
    active_staff_count: number;
  };
  staff_roster?: MonthlyStaffPayout[];
}

export interface MonthlyDisbursePayload {
  month?: string;
  staff_id?: number;
  job_ids?: number[];
}

const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb is not initialized yet
  }
};

export interface StaffJobAnalytics {
  role: "staff";
  total_jobs: number;
  completed_jobs: number;
  pending_jobs: number;
  approved_jobs_count?: number;
  approved_commission: number;
  paid_commission: number;
  pending_monthly_payout: number;
  awaiting_approval_commission: number;
  awaiting_approval_count?: number;
}

export interface SuperAdminJobAnalytics {
  role: "superadmin";
  total_jobs: number;
  completed_jobs: number;
  pending_jobs: number;
  gross_revenue: number;
  total_costs: number;
  total_commissions: number;
  approved_commissions: number;
  pending_commissions: number;
  paid_commissions: number;
  pending_monthly_payout: number;
  net_profit: number;
}

export type JobAnalytics = StaffJobAnalytics | SuperAdminJobAnalytics;

export interface PaginatedResponse<T, A = JobAnalytics> {
  success: boolean;
  message: string;
  data: T[];
  analytics?: A;
  count: number;
  next: string | null;
  previous: string | null;
}

export const fetchJobs = async (
  month?: string,
  page?: number,
  pageSize?: number,
  scope?: 'personal' | 'all'
): Promise<Job[]> => {
  try {
    const client = getClient();
    if (!client) return [];
    const params = new URLSearchParams();
    if (month && month !== 'ALL') params.append('month', month);
    if (page) params.append('page', String(page));
    if (pageSize) params.append('page_size', String(pageSize));
    if (scope) params.append('scope', scope);

    const queryStr = params.toString();
    const url = queryStr ? `/jobs/?${queryStr}` : "/jobs/";
    const res = await client.get(url);

    let items: Job[] = [];
    if (res?.data && Array.isArray(res.data.data)) {
      items = res.data.data;
    } else if (Array.isArray(res?.data)) {
      items = res.data;
    }

    if (res?.data?.analytics) {
      (items as any).analytics = res.data.analytics;
    }

    return items;
  } catch (err) {
    console.error("fetchJobs error:", err);
    return [];
  }
};

export interface FetchJobsParams {
  month?: string;
  page?: number;
  pageSize?: number;
  scope?: 'personal' | 'all';
  search?: string;
  status?: string;
  job_type?: string;
  staff?: string | number;
  referral_source?: string;
  agent?: string | number;
  payout?: string;
  approval?: string;
  payment_status?: string;
  signal?: AbortSignal;
}

export const fetchJobsPaginated = async (
  paramsOrMonth?: FetchJobsParams | string,
  page?: number,
  pageSize?: number,
  scope?: 'personal' | 'all',
  signal?: AbortSignal
): Promise<PaginatedResponse<Job, any>> => {
  try {
    const client = getClient();
    if (!client) {
      return {
        success: false,
        message: "Client not available",
        data: [],
        count: 0,
        next: null,
        previous: null,
      };
    }
    const queryParams = new URLSearchParams();

    if (typeof paramsOrMonth === "object" && paramsOrMonth !== null) {
      const p = paramsOrMonth;
      if (p.month && p.month !== 'ALL') queryParams.append('month', p.month);
      if (p.page) queryParams.append('page', String(p.page));
      if (p.pageSize) queryParams.append('page_size', String(p.pageSize));
      if (p.scope) queryParams.append('scope', p.scope);
      if (p.search && p.search.trim()) queryParams.append('search', p.search.trim());
      if (p.status && p.status !== 'ALL') queryParams.append('status', p.status);
      if (p.job_type && p.job_type !== 'ALL') queryParams.append('job_type', p.job_type);
      if (p.staff && p.staff !== 'ALL') queryParams.append('staff', String(p.staff));
      if (p.referral_source && p.referral_source !== 'ALL') queryParams.append('referral_source', p.referral_source);
      if (p.agent && p.agent !== 'ALL') queryParams.append('agent', String(p.agent));
      if (p.payout && p.payout !== 'ALL') queryParams.append('payout', p.payout);
      if (p.approval && p.approval !== 'ALL') queryParams.append('approval', p.approval);
      if (p.payment_status && p.payment_status !== 'ALL') queryParams.append('payment_status', p.payment_status);
    } else {
      if (typeof paramsOrMonth === 'string' && paramsOrMonth !== 'ALL') {
        queryParams.append('month', paramsOrMonth);
      }
      if (page) queryParams.append('page', String(page));
      if (pageSize) queryParams.append('page_size', String(pageSize));
      if (scope) queryParams.append('scope', scope);
    }

    const queryStr = queryParams.toString();
    const url = queryStr ? `/jobs/?${queryStr}` : "/jobs/";
    const activeSignal =
      typeof paramsOrMonth === "object" && paramsOrMonth !== null
        ? paramsOrMonth.signal || signal
        : signal;
    const res = await client.get(url, { signal: activeSignal });

    if (res?.data && Array.isArray(res.data.data)) {
      return res.data;
    }
    return {
      success: true,
      message: "Jobs retrieved successfully",
      data: Array.isArray(res?.data) ? res.data : [],
      analytics: res?.data?.analytics,
      count: Array.isArray(res?.data) ? res.data.length : (res?.data?.count || 0),
      next: res?.data?.next || null,
      previous: res?.data?.previous || null,
    };
  } catch (err: any) {
    console.error("fetchJobsPaginated error:", err);
    return {
      success: false,
      message: err?.message || "Failed to fetch paginated jobs",
      data: [],
      count: 0,
      next: null,
      previous: null,
    };
  }
};

export const fetchJobDetail = async (id: number): Promise<Job> => {
  const client = getClient();
  const res = await client.get(`/jobs/${id}/`);
  return res.data;
};

export const createJob = async (payload: JobPayload): Promise<Job> => {
  const client = getClient();
  const res = await client.post("/jobs/", payload);
  return res.data;
};

export const updateJob = async (id: number, payload: Partial<JobPayload>): Promise<Job> => {
  const client = getClient();
  const res = await client.patch(`/jobs/${id}/`, payload);
  return res.data;
};

export const deleteJob = async (id: number, scope?: 'personal' | 'all'): Promise<void> => {
  const client = getClient();
  const url = scope ? `/jobs/${id}/?scope=${scope}` : `/jobs/${id}/`;
  await client.delete(url);
};

export const fetchAgents = async (signal?: AbortSignal): Promise<Agent[]> => {
  const client = getClient();
  const res = await client.get("/agents/", { signal });
  return res.data;
};

export const createAgent = async (payload: { name: string; phone_number?: string; email?: string }): Promise<Agent> => {
  const client = getClient();
  const res = await client.post("/agents/", payload);
  return res.data;
};

export const toggleCommissionApproval = async (id: number, is_approved: boolean): Promise<Job> => {
  const client = getClient();
  const res = await client.patch(`/jobs/${id}/`, { is_approved });
  return res.data;
};

export const toggleCommissionPaid = async (id: number, is_commission_paid: boolean): Promise<Job> => {
  const client = getClient();
  const res = await client.patch(`/jobs/${id}/`, { is_commission_paid });
  return res.data;
};

export const updateJobCost = async (id: number, job_cost: string | number): Promise<Job> => {
  const client = getClient();
  const res = await client.patch(`/jobs/${id}/`, { job_cost });
  return res.data;
};

export const fetchMonthlyPayoutSummary = async (month?: string, signal?: AbortSignal): Promise<MonthlyPayoutSummary> => {
  const client = getClient();
  const url = month ? `/jobs/monthly-payout/?month=${month}` : "/jobs/monthly-payout/";
  const res = await client.get(url, { signal });
  return res.data;
};

export const disburseMonthlyCommission = async (
  payload: MonthlyDisbursePayload
): Promise<{ detail: string; count: number; total_paid: string; month?: string }> => {
  const client = getClient();
  const res = await client.post("/jobs/monthly-payout/", payload);
  return res.data;
};

export interface LedgerItem {
  id: number | string;
  date: string;
  item: string;
  amount: string;
  notes?: string;
  is_system?: boolean;
}

export interface StaffPayoutBreakdown {
  staff_id: number | null;
  staff_name: string;
  jobs_count: number;
  commission: string;
  approved_commission?: string;
  disbursed_commission?: string;
  is_disbursed?: boolean;
}

export interface ServiceProfitBreakdown {
  service_key: string;
  service_label: string;
  count: number;
  revenue: string;
  billed?: string;
  portal_cost: string;
  net_revenue?: string;
  commission: string;
  profit: string;
}

export interface MonthlyTLedgerResponse {
  month: string;
  expenses: LedgerItem[];
  incomes: LedgerItem[];
  totals: {
    total_expenses: string;
    total_incomes: string;
    net_balance: string;
    is_surplus: boolean;
    manual_expenses_sum: string;
    manual_incomes_sum: string;
    staff_payout_expenditure: string;
    total_staff_payout?: string;
    disbursed_commission?: string;
    pending_commission?: string;
    total_job_revenue: string;
    total_job_net_revenue?: string;
    total_job_billed?: string;
    total_job_cost: string;
    total_job_commission: string;
    total_job_profit: string;
  };
  service_profit_breakdown: ServiceProfitBreakdown[];
  staff_payouts?: StaffPayoutBreakdown[];
}

export const fetchMonthlyTLedger = async (month?: string, scope?: string, signal?: AbortSignal): Promise<MonthlyTLedgerResponse> => {
  const client = getClient();
  const params = new URLSearchParams();
  if (month) params.append("month", month);
  if (scope) params.append("scope", scope);
  const query = params.toString() ? `?${params.toString()}` : "";
  const res = await client.get(`/finance/t-ledger/${query}`, { signal });
  return res.data;
};

export const createLedgerEntry = async (payload: {
  entry_type: "EXPENSE" | "INCOME";
  item_name: string;
  amount: number | string;
  date?: string;
  notes?: string;
}): Promise<any> => {
  const client = getClient();
  const res = await client.post("/finance/ledger-entry/", payload);
  return res.data;
};

export const deleteLedgerEntry = async (id: number): Promise<void> => {
  const client = getClient();
  await client.delete(`/finance/ledger-entry/${id}/`);
};

// ── Service Rates (Database-backed Services) ─────────────────────────────────

export const fetchServiceRates = async (showAll: boolean = false, signal?: AbortSignal): Promise<ServiceRate[]> => {
  const client = getClient();
  const url = showAll ? "/service-rates/?all=true" : "/service-rates/";
  const res = await client.get(url, { signal });
  return res.data;
};

export const createServiceRate = async (payload: Partial<ServiceRate>): Promise<ServiceRate> => {
  const client = getClient();
  const res = await client.post("/service-rates/", payload);
  return res.data;
};

export const updateServiceRate = async (id: number, payload: Partial<ServiceRate>): Promise<ServiceRate> => {
  const client = getClient();
  const res = await client.patch(`/service-rates/${id}/`, payload);
  return res.data;
};

export const deleteServiceRate = async (id: number): Promise<void> => {
  const client = getClient();
  await client.delete(`/service-rates/${id}/`);
};

// ── Multi-Staff Task Handoff & Commission Split ──────────────────────────────

export const handoffDeliverable = async (
  jobId: number,
  payload: HandoffPayload,
  scope?: string
): Promise<{ message: string; deliverable: Job }> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");
  const query = scope ? `?scope=${scope}` : "";
  const res = await client.post(`/jobs/${jobId}/handoff/${query}`, payload);
  return res.data;
};

// Aliases for convenience & backward compatibility
export type StaffJobItem = Job;
export const fetchStaffJobs = fetchJobsPaginated;
export const fetchMonthlyLedger = fetchMonthlyTLedger;
export const fetchAgentList = fetchAgents;
export const createStaffJob = createJob;
export const updateStaffJob = updateJob;
export const deleteStaffJob = deleteJob;


